<template>
    <div class="steffi-profile-tab v-application vuetify-wrapper">
        <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>

        <v-alert v-if="error" type="error" dense outlined class="mt-4">
            Steffi Daten konnten nicht geladen werden.
        </v-alert>

        <section v-if="!isLoading && !error">
            <v-checkbox
                v-model="offlineTrial"
                label="Offline-Testphase"
                :disabled="offlineTrialSaving"
                :loading="offlineTrialSaving"
                hide-details
                data-test="steffi-offline-trial"
                @change="saveOfflineTrial"
            />
            <p class="text-caption text--secondary mb-4">
                Das Aktivieren beendet eine laufende oder angefragte FPP-Testphase und verhindert weitere
                FPP-Testphasen. Eine reguläre kostenpflichtige Buchung bleibt möglich.
            </p>

            <v-card v-if="!isRestricted" outlined rounded="lg" class="mb-4" data-test="steffi-contact-email-card">
                <v-card-text class="d-flex align-center py-3">
                    <div v-if="!steffiEmailEditing" class="flex-grow-1">
                        <div class="text-caption text--secondary">Steffi-E-Mail für die Steffi-Kommunikation</div>
                        <strong data-test="steffi-contact-email-display">{{ steffiEmail || 'Nicht festgelegt' }}</strong>
                    </div>
                    <template v-if="steffiEmailEditing">
                    <v-text-field
                        v-model="steffiEmailDraft"
                        label="Steffi-E-Mail-Adresse"
                        type="email"
                        outlined
                        dense
                        clearable
                        :rules="[optionalEmailRule]"
                        :disabled="steffiEmailSaving"
                        hide-details
                        class="mr-2"
                        data-test="steffi-contact-email"
                    />
                    <v-btn
                        icon small color="primary" :loading="steffiEmailSaving"
                        data-test="steffi-contact-email-save" @click="saveSteffiEmail()"
                        aria-label="Steffi-E-Mail-Adresse speichern"
                    ><v-icon small>mdi-check</v-icon></v-btn>
                    <v-btn
                        icon small :disabled="steffiEmailSaving"
                        data-test="steffi-contact-email-cancel" @click="cancelSteffiEmailEdit"
                        aria-label="Bearbeiten abbrechen"
                    ><v-icon small>mdi-close</v-icon></v-btn>
                    </template>
                    <v-btn
                        v-else icon small data-test="steffi-contact-email-edit"
                        aria-label="Steffi-E-Mail-Adresse bearbeiten" @click="startSteffiEmailEdit"
                    ><v-icon small>mdi-pencil</v-icon></v-btn>
                </v-card-text>
            </v-card>

            <steffi-admin-onboarding
                v-if="canCreateAdminOnboarding"
                :pharmacy-id="pharmacyId"
                :context="context"
                :route-generation="steffiContextRequestGeneration"
                @context-updated="onChildContextUpdated"
            />

            <steffi-subscribed-pharmacy-view
                v-else
                :pharmacy-id="pharmacyId"
                :context="context"
                :route-generation="steffiContextRequestGeneration"
                :steffi-email-saving="steffiEmailSaving"
                :steffi-email-save-success="steffiEmailSaveSuccess"
                @context-updated="onChildContextUpdated"
                @save-steffi-email="saveSteffiEmail"
            />
        </section>
    </div>
</template>

<script>
import axios from 'axios';
import { mapMutations } from 'vuex';
import { validators } from '../../../validators';
import SteffiAdminOnboarding from './SteffiAdminOnboarding.vue';
import SteffiSubscribedPharmacyView from './SteffiSubscribedPharmacyView.vue';

export default {
    name: 'SteffiProfileTab',
    components: {
        SteffiAdminOnboarding,
        SteffiSubscribedPharmacyView,
    },
    props: {
        pharmacyId: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        context: null,
        isLoading: false,
        error: false,
        offlineTrial: false,
        offlineTrialSaving: false,
        steffiEmail: '',
        steffiEmailDraft: '',
        steffiEmailEditing: false,
        steffiEmailSaving: false,
        steffiEmailSaveSuccess: null,
        steffiContextRequestGeneration: 0,
    }),
    computed: {
        canCreateAdminOnboarding() {
            return Boolean(this.context && this.context.can_create_admin_onboarding);
        },
        isRestricted() {
            return this.context?.access_scope === 'steffi_team';
        },
    },
    watch: {
        pharmacyId() {
            this.steffiEmailSaving = false;
            this.steffiEmailEditing = false;
            this.steffiEmailSaveSuccess = null;
            this.fetchSteffiContext();
        },
    },
    created() {
        this.fetchSteffiContext();
    },
    methods: {
        onContextUpdated(context) {
            this.context = context;
            this.offlineTrial = Boolean(context?.pharmacy?.steffi_offline_trial);
            this.steffiEmail = context?.pharmacy?.steffi_email || '';
            if (!this.steffiEmailEditing) this.steffiEmailDraft = this.steffiEmail;
        },
        onChildContextUpdated(context, pharmacyId, routeGeneration) {
            if (
                this.pharmacyId !== pharmacyId
                || this.steffiContextRequestGeneration !== routeGeneration
            ) return;
            this.onContextUpdated(context);
        },
        optionalEmailRule(value) {
            return !String(value || '').trim() || validators.email(value);
        },
        startSteffiEmailEdit() {
            this.steffiEmailDraft = this.steffiEmail || this.context?.pharmacy?.invitation_email || '';
            this.steffiEmailEditing = true;
        },
        cancelSteffiEmailEdit() {
            this.steffiEmailDraft = this.steffiEmail;
            this.steffiEmailEditing = false;
        },
        async saveSteffiEmail(request = null) {
            const childRequest = request && typeof request === 'object' && request.requestId
                ? request
                : null;
            const email = String(childRequest ? childRequest.email : this.steffiEmailDraft || '').trim();
            if (
                this.steffiEmailSaving
                || !this.pharmacyId
                || this.optionalEmailRule(email) !== true
            ) return;
            const pharmacyId = this.pharmacyId;
            const routeGeneration = this.steffiContextRequestGeneration;
            if (childRequest && (
                childRequest.pharmacyId !== pharmacyId
                || childRequest.routeGeneration !== routeGeneration
            )) return;
            this.steffiEmailDraft = email;
            this.steffiEmailSaving = true;
            try {
                const response = await axios.put(`/api/pharmacies/${pharmacyId}/steffi/admin-context`, {
                    steffi_email: email,
                });
                if (
                    this.pharmacyId !== pharmacyId
                    || this.steffiContextRequestGeneration !== routeGeneration
                ) return;
                this.onContextUpdated(response.data);
                this.steffiEmailEditing = false;
                if (childRequest) {
                    this.steffiEmailSaveSuccess = {
                        pharmacyId,
                        routeGeneration,
                        requestId: childRequest.requestId,
                        email: response.data?.pharmacy?.steffi_email || '',
                    };
                }
                this.alertSuccess('Steffi-E-Mail-Adresse gespeichert.');
            } catch (error) {
                if (
                    this.pharmacyId !== pharmacyId
                    || this.steffiContextRequestGeneration !== routeGeneration
                ) return;
                this.alertError(error?.response?.data?.message || 'Die Steffi-E-Mail-Adresse konnte nicht gespeichert werden.');
            } finally {
                if (
                    this.pharmacyId === pharmacyId
                    && this.steffiContextRequestGeneration === routeGeneration
                ) this.steffiEmailSaving = false;
            }
        },
        async saveOfflineTrial(value) {
            if (this.offlineTrialSaving || !this.pharmacyId) return;
            const pharmacyId = this.pharmacyId;
            this.offlineTrialSaving = true;
            try {
                const response = await axios.put(`/api/pharmacies/${pharmacyId}/steffi/admin-context`, {
                    steffi_offline_trial: Boolean(value),
                });
                if (this.pharmacyId !== pharmacyId) return;
                this.onContextUpdated(response.data);
                this.alertSuccess('Offline-Testphase gespeichert');
            } catch (error) {
                if (this.pharmacyId !== pharmacyId) return;
                this.offlineTrial = Boolean(this.context?.pharmacy?.steffi_offline_trial);
                this.alertError(error?.response?.data?.message || 'Offline-Testphase konnte nicht gespeichert werden.');
            } finally {
                this.offlineTrialSaving = false;
            }
        },
        async fetchSteffiContext() {
            const pharmacyId = this.pharmacyId;
            const requestGeneration = ++this.steffiContextRequestGeneration;
            if (!pharmacyId) {
                this.isLoading = false;
                return;
            }
            try {
                this.isLoading = true;
                this.error = false;
                const response = await axios.get(`/api/pharmacies/${pharmacyId}/steffi/admin-context`);
                if (
                    this.steffiContextRequestGeneration !== requestGeneration
                    || this.pharmacyId !== pharmacyId
                ) return;
                this.onContextUpdated(response.data);
            } catch (error) {
                if (
                    this.steffiContextRequestGeneration !== requestGeneration
                    || this.pharmacyId !== pharmacyId
                ) return;
                this.error = true;
                this.alertError();
                console.log('failed to fetch Steffi profile context', error);
            } finally {
                if (
                    this.steffiContextRequestGeneration === requestGeneration
                    && this.pharmacyId === pharmacyId
                ) this.isLoading = false;
            }
        },
        ...mapMutations({
            alertSuccess: 'alert/success',
            alertError: 'alert/error',
        }),
    },
}
</script>

<style scoped>
.steffi-profile-tab > section {
    width: 100%;
    padding: 0 1rem;
}
</style>
